///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Authoring a RenderScene (shared-types/render-scene.ts) from the live
/// app: frame the shot, turn on the layers, open a cutout if wanted, then
/// export. The scene captures what the redux store knows (camera, visible
/// layers, focused target, open cutout, scene clock) and leaves the
/// renderer's per-kind template to fill in the rest (shots, overlay
/// details, data attribution). Dev-only entry point: __ebDebug.exportRenderScene().
///

import { CameraParams, EarthCameraState } from "@/earthbrowser/activities/EarthActivity/EarthCameraState"
import { type DatasetLayerManager } from "@/earthbrowser/dataset-distribution/DatasetLayerManager"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { appStore } from "@/redux/store"
import { cEmbedTargetTypes } from "@/utils/embedLayers"
import { parseTarget } from "@/utils/navigationHistory"
import { viewShareUrl } from "@/utils/savedViews"
import { trig } from "@stovokor/math/math"
import { cRenderSceneVersion, type RenderClock, type RenderCutout, type RenderScene, type RenderSubject, type RenderSubjectKind } from "@shared-types"

/** Clip length in scene time when the export has nothing better to go on (matches VIDEO_SPAN_HOURS) */
const cDefaultSpanHours = 12
/** Step between frames (matches VIDEO_STEP_MINUTES) */
const cDefaultStepMinutes = 2

export interface ExportRenderSceneOptions {
	/** Hours of scene time before the clock's current time (default 12) */
	spanHours?: number
	stepMinutes?: number
	style?: RenderScene["style"]
	format?: RenderScene["format"]
	/** Capture against this embed instead of production (e.g. the dev server) */
	appUrl?: string
}

/**
 * Snapshot the live view as a render scene. The subject is the focused
 * ?target= entity when there is one, otherwise a coords subject at the
 * camera fulcrum (the point at screen center). The clock window ends at
 * the scene clock's current time - scrub the timeline to the moment the
 * clip should end before exporting.
 */
export function exportRenderScene(options: ExportRenderSceneOptions = {}): RenderScene {
	const state = appStore.getState()
	const camera = state.earthbrowser.cameraState
	const params = new CameraParams(camera.radius, camera.theta, camera.phi, camera.tilt, camera.bearing)
	const fulcrum = new geographic(EarthCameraState.fulcrumForParams(params))
	const lookAt = { lat: fulcrum.latitude * trig.deg, lon: fulcrum.longitude * trig.deg }

	const target = new URL(window.location.href).searchParams.get("target")
	const infoWindow = state.userInterface.infoWindow
	const focusName = target && infoWindow.isOpen ? infoWindow.title.trim() : ""
	const subject = subjectFor(target, lookAt, focusName || undefined, state.datasetScene.layerManager ?? undefined)

	const cutout = cutoutFor(state.dialog.quakeCutout?.quakeId, state.dialog.stormCutout?.stormId)

	const layers = [...state.earthbrowser.visibleDatasets]
	// A cutout's own layer must be on for the block to build
	if (cutout?.kind === "quake" && !layers.includes("earthquake")) layers.push("earthquake")
	if (cutout?.kind === "storm" && !layers.includes("hurricane")) layers.push("hurricane")

	const scene: RenderScene = {
		version: cRenderSceneVersion,
		subject,
		camera: {
			lookAt,
			altitudeKm: round(camera.radius - wgs.a, 1),
			tiltDeg: round(camera.tilt * trig.deg, 2),
			bearingDeg: round(camera.bearing * trig.deg, 2),
			pose: { ...camera },
		},
		layers,
		layerSettings: { stormWindProbabilityKt: state.dialog.stormWindProbabilityKt },
		clock: clockFor(state.earthbrowser.clockState, state.earthbrowser.clockCurrentTime, options),
		style: options.style ?? "clean",
		format: options.format ?? "reel",
		overlay: subject.name ? { title: subject.name } : undefined,
		authored: {
			at: new Date().toISOString(),
			appVersion: __APP_VERSION__,
			url: viewShareUrl({ camera, layers, focusTargetId: target ?? undefined }),
		},
	}
	if (cutout) scene.cutout = cutout
	if (options.appUrl) scene.appUrl = options.appUrl
	return scene
}

/** Export and put the pretty-printed JSON on the clipboard; resolves with the scene */
export async function copyRenderScene(options: ExportRenderSceneOptions = {}): Promise<RenderScene> {
	const scene = exportRenderScene(options)
	await navigator.clipboard.writeText(JSON.stringify(scene, null, 2))
	return scene
}

function subjectFor(
	target: string | null,
	lookAt: { lat: number; lon: number },
	name: string | undefined,
	layerManager: DatasetLayerManager | undefined
): RenderSubject {
	const parsed = target ? parseTarget(target) : null
	if (parsed?.kind === "coords") {
		return { kind: "coords", target: target ?? undefined, lat: parsed.lat, lon: parsed.lon, name: name ?? parsed.name }
	}
	if (parsed?.kind === "entity" && isSubjectKind(parsed.type)) {
		// The entity's own position when its scene object is built (the
		// focus flight may still be under way, so the fulcrum can lag it)
		const position = entityPosition(layerManager, parsed.type, parsed.id) ?? lookAt
		return { kind: parsed.type, id: parsed.id, target: target ?? undefined, lat: position.lat, lon: position.lon, name }
	}
	return { kind: "coords", lat: lookAt.lat, lon: lookAt.lon, name }
}

/** Ground position under a dataset entity's scene node, if the layer has built it */
function entityPosition(layerManager: DatasetLayerManager | undefined, type: string, id: string): { lat: number; lon: number } | undefined {
	const layerName = cEmbedTargetTypes[type]?.layerName
	if (!layerManager || !layerName) return undefined
	const node = layerManager.getSceneObject(layerName, id)
	if (!node || node.worldPosition.length === 0) return undefined
	const geo = new geographic(node.worldPosition)
	return { lat: geo.latitude * trig.deg, lon: geo.longitude * trig.deg }
}

/** Every entity type parseTarget can produce (its prefix map) is a subject kind */
const cSubjectKinds: ReadonlySet<string> = new Set<RenderSubjectKind>([
	"hurricane", "earthquake", "volcano", "wildfire", "alert", "buoy",
	"webcam", "satellite", "location", "placemark", "nexrad",
])

function isSubjectKind(type: string): type is RenderSubjectKind {
	return cSubjectKinds.has(type)
}

function cutoutFor(quakeId: string | undefined, stormId: string | undefined): RenderCutout | undefined {
	if (quakeId) return { kind: "quake", id: quakeId }
	if (stormId) return { kind: "storm", id: stormId }
	return undefined
}

/** Window ending at the scene clock (real-time = now), spanning back spanHours */
function clockFor(clockState: string, clockCurrentTime: number, options: ExportRenderSceneOptions): RenderClock {
	const end = clockState === "real-time" ? Date.now() : clockCurrentTime
	const spanHours = options.spanHours ?? cDefaultSpanHours
	return {
		start: end - spanHours * 3600_000,
		end,
		stepMinutes: options.stepMinutes ?? cDefaultStepMinutes,
	}
}

function round(value: number, digits: number): number {
	const f = 10 ** digits
	return Math.round(value * f) / f
}
